/* File : src/components/common/QRCode.js */
import React, { useEffect, useRef } from 'react';
import QRCodeLib from 'qrcode';
import './QRCode.css';

/**
 * Composant pour générer et afficher un QR code
 * 
 * @param {Object} props - Propriétés du composant
 * @param {string} props.data - Données à encoder dans le QR code
 * @param {number} props.size - Taille du QR code en pixels
 * @param {string} props.logo - URL optional du logo à placer au centre du QR code
 * @param {string} props.logoSize - Taille du logo en pixels
 * @param {string} props.className - Classes CSS supplémentaires
 */
const QRCode = ({
  data,
  size = 200,
  logo = '',
  logoSize = 40,
  className = '',
}) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!data || !canvasRef.current) return;

    const generateQRCode = async () => {
      try {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');

        // Effacer le canvas
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // Générer le QR code
        await QRCodeLib.toCanvas(canvas, data, {
          width: size,
          margin: 2,
          color: {
            dark: '#000000',  // Couleur des points (noir Guinness)
            light: '#FFFFFF'  // Couleur du fond (blanc)
          },
          errorCorrectionLevel: 'H' // Niveau de correction d'erreur élevé pour supporter le logo
        });

        // Ajouter le logo au centre si fourni
        if (logo) {
          const img = new Image();
          img.onload = () => {
            // Position du logo au centre
            const centerX = (size - logoSize) / 2;
            const centerY = (size - logoSize) / 2;

            // Dessiner un cercle blanc de fond pour le logo
            ctx.fillStyle = '#FFFFFF';
            ctx.beginPath();
            ctx.arc(size / 2, size / 2, logoSize / 2 + 5, 0, 2 * Math.PI);
            ctx.fill();

            // Dessiner le logo
            ctx.drawImage(img, centerX, centerY, logoSize, logoSize);
          };
          img.src = logo;
        }
      } catch (error) {
        console.error('Error generating QR code:', error);
      }
    };

    generateQRCode();
  }, [data, size, logo, logoSize]);

  const containerClasses = [
    'guinness-qrcode-container',
    className
  ].filter(Boolean).join(' ');

  return (
    <div className={containerClasses}>
      <canvas 
        ref={canvasRef}
        width={size}
        height={size}
        className="guinness-qrcode-canvas"
      />
    </div>
  );
};

export default QRCode;